/**
 * Zustand-стор уведомлений.
 *
 * Экраны кладут сюда короткое сообщение; показывает их единственный
 * компонент `ToastHost`. Стор не рисует и не ходит в сеть — только хранит
 * очередь и время жизни сообщений:
 *   info / success / warning гаснут сами через 3 секунды;
 *   error остаётся, пока ученик его не закроет — ошибку важно прочитать;
 *   на экране не больше трёх тостов, а повтор того же текста не плодит
 *   копию, а обновляет уже показанный.
 */

import { create } from 'zustand';

/** Тон сообщения определяет цвет и иконку тоста. */
export type ToastTone = 'info' | 'success' | 'warning' | 'error';

/** Одно уведомление в очереди. */
export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
  /** Через сколько миллисекунд тост погаснет сам; `null` — только вручную. */
  readonly ttl: number | null;
}

/** Сколько миллисекунд тост остаётся на экране. */
export const TOAST_TTL_MS = 3000;
/** Больше тостов одновременно не показываем — старые уходят. */
const MAX_TOASTS = 3;

interface ToastStore {
  readonly toasts: readonly Toast[];
  /** Показывает сообщение; возвращает id, чтобы его можно было снять. */
  push: (message: string, tone?: ToastTone) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;
const timers = new Map<number, number>();

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],

  push: (message, tone = 'info') => {
    const id = nextId;
    nextId += 1;
    const ttl = tone === 'error' ? null : TOAST_TTL_MS;

    // Тот же текст уже на экране — заменяем его, таймер начинается заново.
    const duplicate = get().toasts.find((item) => item.message === message);
    if (duplicate !== undefined) get().dismiss(duplicate.id);

    const queue = [...get().toasts, { id, message, tone, ttl }];
    for (const stale of queue.slice(0, Math.max(0, queue.length - MAX_TOASTS))) get().dismiss(stale.id);
    set({ toasts: queue.slice(-MAX_TOASTS) });
    // Автоснятие по таймеру: экран не обязан помнить о уборке тоста.
    if (ttl !== null) timers.set(id, window.setTimeout(() => get().dismiss(id), ttl));

    return id;
  },

  dismiss: (id) => {
    window.clearTimeout(timers.get(id));
    timers.delete(id);
    set({ toasts: get().toasts.filter((toast) => toast.id !== id) });
  },
}));

/** Короткие обёртки — экраны вызывают их в `catch`, без импорта стора. */
export const toast = {
  info: (message: string): void => {
    useToastStore.getState().push(message, 'info');
  },
  success: (message: string): void => {
    useToastStore.getState().push(message, 'success');
  },
  warning: (message: string): void => {
    useToastStore.getState().push(message, 'warning');
  },
  error: (message: string): void => {
    useToastStore.getState().push(message, 'error');
  },
};
